import { Component, Input, OnDestroy, OnInit } from "@angular/core";

import { TranslateService } from "@ngx-translate/core";

import { map } from "rxjs/operators";
import { select, Store } from "@ngrx/store";
import { AppState, movementsStateTableConfig } from "src/app/store/reducers";
import {
  getContractMovementsList,
  getMovementsExcelList,
  resetMovementsState,
} from "src/app/store/actions/movements/movements.actions";

import {
  ColumnSorterInfo,
  DefaultPage,
  ExportEvent,
  FilterEvent,
  FilterName,
  PageEvent,
  SearchEvent,
  SortEvent,
  TableEvent,
} from "src/app/models/custom-table/custom-table.model";
import {
  removeColumnSorterInfoOptions,
  removeTableConfigOptions,
  setAdvancedFilter,
} from "src/app/shared/utils/tableUtils";

import { BaseMovementsList } from "src/app/shared/base-classes/base-movements-list/base-movements-list";

const USAGE_CODE = "CUSTOMER.MOVEMENT";

@Component({
  selector: "customer-movements-list",
  templateUrl: "./customer-movements-list.component.html",
  styleUrls: ["./customer-movements-list.component.scss"],
})
export class CustomerMovementsListComponent
  extends BaseMovementsList
  implements OnInit, OnDestroy
{
  @Input() set code(code: string) {
    if (code) {
      this._code = code;
      this.getTableConfig(USAGE_CODE);
      this.setTableCustomElements();
      this.getMovementsList(null, this.initialSort);
    }
  }

  _code: string;

  constructor(store: Store<AppState>, translate: TranslateService) {
    super(store, translate);

    this.columnInfo$ = this.store.pipe(select(movementsStateTableConfig)).pipe(
      map((columnInfo) => {
        if (columnInfo) {
          return removeColumnSorterInfoOptions(columnInfo);
        }
      })
    );
  }

  ngOnInit(): void {
    this.setTranslatedTitle();
    this.tableConfig = removeTableConfigOptions(this.tableConfig);
  }

  getMovementsList(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    const advancedFilter = this.setAdvancedFilter(filterEvent);

    this.store.dispatch(
      getContractMovementsList({
        options: {
          paging: {
            skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
            top: pageSize ?? DefaultPage.PAGE_SIZE,
            realSize: true,
          },
          filteringArgs: searchEvent,
          sortingArgs: sortEvent,
          advancedFilteringArgs: advancedFilter,
        },
      })
    );
  }

  getMovementsExcelList(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    const advancedFilter = this.setAdvancedFilter(filterEvent);

    this.store.dispatch(
      getMovementsExcelList({
        options: {
          paging: {
            skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
            top: pageSize ?? DefaultPage.EXCEL_PAGE_SIZE,
          },
          filteringArgs: searchEvent,
          sortingArgs: sortEvent,
          advancedFilteringArgs: advancedFilter,
        },
      })
    );
  }

  setAdvancedFilter(filterEvent: FilterEvent) {
    const customerMovementsFilter = {
      column: "contract.customer.code",
      operator: FilterName.EQUALS_TO,
      value: this._code,
    };

    return setAdvancedFilter(customerMovementsFilter, filterEvent);
  }

  onGetTableData(tableEvent: TableEvent) {
    this.getTableData(tableEvent, this.getMovementsList.bind(this));
  }

  onGetExportData(exportEvent: ExportEvent) {
    this.getExportData(exportEvent, this.getMovementsExcelList.bind(this));
  }

  onSaveTableConfig(columnInfo: ColumnSorterInfo[]) {
    this.saveTableConfig(columnInfo, USAGE_CODE);
  }

  ngOnDestroy() {
    if (this.movementsListSub) {
      this.movementsListSub.unsubscribe();
    }
    this.store.dispatch(resetMovementsState());
  }
}
